import api from '@/lib/api';
import { API_ENDPOINTS } from '@/constants/endpoints';
import type { ApiResponse } from '@/types';
import type { CheckSources, ToolRef } from './accountIntentConfig.service';

/** One turn Kim was set to answer on its own, sent or held back, as kept by the backend (automationReplyLog.service.ts). */
export interface AutomationReply {
	id: string;
	buId: number | null;
	provider: string;
	ticketId: string;
	channel: string | null;
	message: string;
	/** Detected on the incoming message, which automation is decided on; labels and the filter use these. */
	intents: string[];
	/** Brahma's final intents for the reply, which can differ. Empty on older entries. */
	finalIntents: string[];
	confidence: number | null;
	requestId: string | null;
	traceId: string | null;
	/** `blocked`: Kim drafted the reply but did not send it, for the `block` reason. */
	outcome: AutomationOutcome;
	block: AutomationBlock | null;
	/** When the reply was sent, or held back. */
	sentAt: string;
	/** A Sentinel's verdict; null until the reply is reviewed. */
	review: AutomationReplyReview | null;
	/** What the reply did against its intents' automation checks; null until checked, or when none apply. */
	checks: AutomationCheckResult | null;
	/**
	 * The gap the turn's trace was investigated into, or null when it is on none. Absent when the
	 * user may not view the gap board, or when the gap lookup failed.
	 */
	gap?: AutomationReplyGap | null;
}

/** A gap as the automation log links it; `status` is the gap board's status code. */
export interface AutomationReplyGap {
	id: string;
	displayId: number | null;
	title: string;
	status: string;
}

/** `no_trace`: the reply's tool calls or sources were not recorded, so the checks could not tell. */
export type AutomationCheckStatus = 'pass' | 'fail' | 'no_trace';

export interface AutomationCheckResult {
	status: AutomationCheckStatus;
	/** The intents whose checks applied. */
	intents: string[];
	tools: {
		/** Required tools it never called. */
		missing: ToolRef[];
		/** Forbidden tools it called. */
		forbidden: ToolRef[];
		/** Every integration call it made; null when they were not recorded. */
		called: ToolRef[] | null;
	};
	sources: {
		/** Required sources it did not draw on. */
		missing: CheckSources;
		/** What it drew on; null when that was not recorded. */
		used: CheckSources | null;
	};
	/** Why the checks could not tell, for `no_trace`. */
	note: string | null;
	checkedAt: string;
}

/** What a run of the checks over past replies did. */
export interface AutomationChecksRunSummary {
	checked: number;
	passed: number;
	failed: number;
	noTrace: number;
	/** Replies none of whose intents has checks. */
	skipped: number;
	/** More replies matched than one run checks; the newest 500 were. */
	capped: boolean;
}

export type AutomationReviewVerdict = 'correct' | 'incorrect';

export type AutomationOutcome = 'sent' | 'blocked';

/** Who held the reply back (Brahma's own checks, or a guard in Kim's backend), and the reason code. */
export interface AutomationBlock {
	by: 'brahma' | 'kim';
	reason: string;
}

export interface AutomationReplyReview {
	verdict: AutomationReviewVerdict;
	reasons: string[];
	note: string | null;
	reviewerName: string | null;
	reviewedAt: string;
}

export interface AutomationReplyPage {
	entries: AutomationReply[];
	hasMore: boolean;
	/** Matching replies in all; sent with the first page only. */
	total?: number;
	/** Open and closed tickets among the matches, whatever `ticket_status` is; first page only. */
	statusCounts?: { open: number; closed: number };
}

export interface AutomationReplyFilter {
	provider?: string;
	q?: string;
	/** ISO instants: sent at or after `from`, before `to`. */
	from?: string;
	to?: string;
	/** `pending` is the review queue. */
	review?: 'pending' | AutomationReviewVerdict;
	outcome?: AutomationOutcome;
	/** Entries that name this intent. */
	intent?: string;
	/** Entries whose automation checks ended so. */
	checks?: AutomationCheckStatus;
	/** The ticket's status in its helpdesk; absent is All. */
	ticket_status?: 'open' | 'closed';
}

export interface AutomationThreadMessage {
	id: number;
	role: 'customer' | 'agent';
	createdAt: string;
	text: string;
}

export const automationReplyService = {
	/** A page of the brand's replies, newest first; `before` is the last entry id already shown. */
	async fetchPage(filter: AutomationReplyFilter, before: string | undefined, scope: { bu_id?: string }) {
		const res = await api.get<ApiResponse<AutomationReplyPage>>(API_ENDPOINTS.AUTOMATION_REPLIES.LIST, {
			params: { ...scope, ...filter, ...(before && { before }), limit: 25 }
		});
		return res.data.data;
	},

	/** The intents the brand's log names, for its intent filter. */
	async fetchIntents(scope: { bu_id?: string }) {
		const res = await api.get<ApiResponse<{ intents: string[] }>>(API_ENDPOINTS.AUTOMATION_REPLIES.INTENTS, { params: scope });
		return res.data.data.intents;
	},

	/** The ticket's conversation as Kim ingested it (empty when it never was), and the conversation's id. */
	async fetchThread(id: string) {
		const res = await api.get<ApiResponse<{ messages: AutomationThreadMessage[]; conversationId?: number | null }>>(
			API_ENDPOINTS.AUTOMATION_REPLIES.THREAD(id)
		);
		return { messages: res.data.data.messages, conversationId: res.data.data.conversationId ?? null };
	},

	/** A Sentinel's verdict on the reply; a reply is reviewed once. */
	async review(id: string, body: { verdict: AutomationReviewVerdict; reasons: string[]; note: string | null }) {
		const res = await api.post<ApiResponse<{ entry: AutomationReply }>>(API_ENDPOINTS.AUTOMATION_REPLIES.REVIEW(id), body);
		return res.data.data.entry;
	},

	/** Checks the brand's replies in the range against their intents' checks, the newest 500, and keeps each result. */
	async runChecks(filter: Pick<AutomationReplyFilter, 'from' | 'to' | 'intent' | 'outcome'>, scope: { bu_id?: string }) {
		const res = await api.post<ApiResponse<AutomationChecksRunSummary>>(API_ENDPOINTS.AUTOMATION_REPLIES.RUN_CHECKS, filter, {
			params: scope
		});
		return res.data.data;
	},

	/** Checks one reply again, with the checks as they are now. */
	async rerunChecks(id: string) {
		const res = await api.post<ApiResponse<{ entry: AutomationReply }>>(API_ENDPOINTS.AUTOMATION_REPLIES.CHECKS(id));
		return res.data.data.entry;
	},

	/** One entry of the account, whichever brand it belongs to (the Slack alert links to it). */
	async fetchOne(id: string) {
		const res = await api.get<ApiResponse<{ entry: AutomationReply }>>(API_ENDPOINTS.AUTOMATION_REPLIES.BY_ID(id));
		return res.data.data.entry;
	}
};
